import { useState, useMemo, useEffect, useRef, lazy, Suspense } from "react";
import { useParams, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { DocumentText, Calendar, Calculator } from "iconsax-react";
import { extractErrorMessage, getMonthName, ACCOUNTING_TIMESHEETS_PAGE_ROUTE } from "@/shared/utils";
import { PERMISSIONS } from "@/shared/consts";
import { useLocale, useAuth, useBreadcrumbOverrides } from "@/shared/hooks";
import { Prompt, PromptForm, Toast, Dropdown, UnsavedChangesModal } from "@/shared/ui";
import { useDispatch } from "react-redux";
import { baseApi } from "@/shared/api";
import { isBudgetLimitErrorV2, isOverrideableError } from "@/features/accounting/smeta/types";
import { useOverrideState } from "@/features/accounting/smeta/hooks/useOverrideState";
import { extractSmetaErrorMessage } from "@/features/accounting/smeta/utils/errorDispatch";
import {
  useGetCombinedTimesheetDetailQuery,
  useApproveTimesheetMutation,
  useDeleteTimesheetMutation,
} from "../../api";
import {
  useGeneratePayrollMutation,
  useApprovePayrollMutation,
  useMarkPayrollPaidMutation,
  useDeletePayrollMutation,
} from "../../api";
import { useTimesheetMutations, useTimesheetDownloads, useTimesheetAllocations, useTimesheetLeaveProtection } from "../../hooks";
import {
  getTimesheetAvailableActions,
  getDaysInMonth,
  buildTimesheetUpdatePayload,
  getEntryStats as getEntryStatsUtil,
  recomputeAllocations,
} from "../../utils";
import type { DailyData } from "../../types";
import type { PayrollEntry } from "../../types";
import {
  TimesheetDetailHeader,
  StatusCodeLegend,
  HolidaysLegend,
  TimesheetMonthStats,
  TimesheetDetailSkeleton,
  TimesheetTableSkeleton,
  TimesheetAllocationsSection,
} from "./components";

const TimesheetEditableTable = lazy(() =>
  import("./components/TimesheetEditableTable").then((m) => ({ default: m.default })),
);
const PayrollStatCards = lazy(() =>
  import("./components/payroll/PayrollStatCards").then((m) => ({ default: m.default })),
);
const AccrualSection = lazy(() =>
  import("./components/payroll/AccrualSection").then((m) => ({ default: m.default })),
);
const AccountingSection = lazy(() =>
  import("./components/payroll/AccountingSection").then((m) => ({ default: m.default })),
);
const ReportingSection = lazy(() =>
  import("./components/payroll/ReportingSection").then((m) => ({ default: m.default })),
);
const PayrollEntryDetailModal = lazy(() =>
  import("./components/payroll/PayrollEntryDetailModal").then((m) => ({ default: m.default })),
);
const GeneratePayrollModal = lazy(() =>
  import("./components/payroll/GeneratePayrollModal").then((m) => ({ default: m.default })),
);
const OverrideReasonModal = lazy(() =>
  import("@/features/accounting/smeta/components/OverrideReasonModal").then((m) => ({ default: m.default })),
);

type ActiveTab = "step1" | "step2" | "step3";

const STEP_TABS = [
  { id: "step1" as const, labelKey: "steps.step1", icon: DocumentText },
  { id: "step2" as const, labelKey: "steps.step2", icon: Calendar },
  { id: "step3" as const, labelKey: "steps.step3", icon: Calculator },
];

export default function TimesheetDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation("TimesheetsPage");
  const dispatch = useDispatch();
  const timesheetId = id ? parseInt(id, 10) : 0;

  const {
    data: timesheet,
    isLoading,
    refetch,
  } = useGetCombinedTimesheetDetailQuery(timesheetId, {
    skip: !timesheetId || isNaN(timesheetId),
  });

  const payroll = timesheet?.payroll ?? null;
  const combinedStatus = timesheet?.combined_status ?? "draft";

  const { handleBatchUpdateEntries } = useTimesheetMutations((prompt) => {
    if (prompt) {
      setPromptState({
        title: prompt.title,
        text: prompt.text,
        variant: prompt.variant || "success",
      });
    } else {
      setPromptState(null);
    }
  });
  const [approveTimesheet, { isLoading: isApproving }] = useApproveTimesheetMutation();
  const [deleteTimesheet, { isLoading: isDeletingTimesheet }] = useDeleteTimesheetMutation();
  const { handleDownloadDocx, handleDownloadPdf, isDownloading } = useTimesheetDownloads((prompt) => {
    if (prompt) {
      setPromptState({
        title: prompt.title,
        text: prompt.text,
        variant: prompt.variant || "success",
      });
    } else {
      setPromptState(null);
    }
  });

  const [generatePayroll, { isLoading: isGenerating }] = useGeneratePayrollMutation();
  const [approvePayroll, { isLoading: isApprovingPayroll }] = useApprovePayrollMutation();
  const [markPayrollPaid, { isLoading: isMarkingPaid }] = useMarkPayrollPaidMutation();
  const [deletePayroll, { isLoading: isDeletingPayroll }] = useDeletePayrollMutation();
  const [pendingChanges, setPendingChanges] = useState<Map<number, DailyData>>(new Map());
  const [promptState, setPromptState] = useState<{
    title: string;
    text: string;
    variant: "success" | "error" | "warning";
  } | null>(null);
  const [confirmApprove, setConfirmApprove] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<ActiveTab>("step1");
  const [helpDismissed, setHelpDismissed] = useState(() => localStorage.getItem("timesheet-help-dismissed") === "true");
  const [step3TooltipOpen, setStep3TooltipOpen] = useState(false);
  const [confirmDeleteTimesheet, setConfirmDeleteTimesheet] = useState(false);
  const [unsavedTargetTab, setUnsavedTargetTab] = useState<ActiveTab | null>(null);
  const [showRecalcPrompt, setShowRecalcPrompt] = useState(false);
  const [showMismatchRecalc, setShowMismatchRecalc] = useState(false);
  const [mismatchTargetTab, setMismatchTargetTab] = useState<ActiveTab | null>(null);
  const mismatchDismissedRef = useRef(false);

  const { t: tSmeta } = useTranslation("SmetaPage");
  const payrollOverride = useOverrideState<{ payrollId: number }>();
  const [confirmApprovePayroll, setConfirmApprovePayroll] = useState(false);
  const [confirmMarkPaid, setConfirmMarkPaid] = useState(false);
  const [confirmDeletePayroll, setConfirmDeletePayroll] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [entryDetailModal, setEntryDetailModal] = useState<PayrollEntry | null>(null);

  const { hasPermission } = useAuth();
  const canEdit = timesheet
    ? getTimesheetAvailableActions(timesheet.status).includes("edit") && hasPermission(PERMISSIONS.HR.EDIT_TIMESHEETS)
    : false;
  const canApprove = hasPermission(PERMISSIONS.HR.APPROVE_TIMESHEETS);
  const canGeneratePayroll = hasPermission(PERMISSIONS.ACCOUNTING.GENERATE_PAYROLL) && timesheet?.status === "approved";
  const canApprovePayrollPerm = hasPermission(PERMISSIONS.ACCOUNTING.APPROVE_PAYROLL);
  const canPayPayroll = hasPermission(PERMISSIONS.ACCOUNTING.PAY_PAYROLL);
  const canDeletePayrollPerm = hasPermission(PERMISSIONS.ACCOUNTING.DELETE_PAYROLL);
  const locale = useLocale();

  const isAnyLoading =
    isSaving || isApproving || isDownloading || isGenerating || isDeletingTimesheet ||
    isApprovingPayroll || isMarkingPaid || isDeletingPayroll;

  useBreadcrumbOverrides(
    timesheet ? { timesheetLabel: `${getMonthName(timesheet.month, locale)} ${timesheet.year}` } : {},
  );

  const daysInMonth = useMemo(() => {
    if (!timesheet) return [];
    return Array.from({ length: getDaysInMonth(timesheet.year, timesheet.month) }, (_, i) => String(i + 1));
  }, [timesheet]);

  const allocationData = useTimesheetAllocations({
    timesheetId,
    entries: timesheet?.entries || [],
    isDraft: timesheet?.status === "draft",
    canEdit,
    setPrompt: (prompt) => {
      if (prompt) {
        setPromptState({
          title: prompt.title,
          text: prompt.text,
          variant: prompt.variant || "success",
        });
      } else {
        setPromptState(null);
      }
    },
  });

  const leaveProtection = useTimesheetLeaveProtection({
    hasPendingChanges: pendingChanges.size > 0,
    onSave: handleSaveChanges,
    onDiscard: () => setPendingChanges(new Map()),
  });

  useEffect(function resetTabOnPayrollDelete() {
    if (activeTab === "step3" && !payroll && !isGenerating) {
      setActiveTab("step1");
    }
  }, [activeTab, payroll, isGenerating]);

  function hasPendingChanges(): boolean {
    return pendingChanges.size > 0;
  }

  async function handleSaveChanges() {
    if (isSaving || !timesheet || !hasPendingChanges()) return;

    try {
      setIsSaving(true);
      mismatchDismissedRef.current = false;
      const updates = buildTimesheetUpdatePayload(pendingChanges, timesheet.entries || []);
      await handleBatchUpdateEntries(updates);
      setPendingChanges(new Map());
      dispatch(baseApi.util.invalidateTags([{ type: "TimesheetAllocations", id: `TIMESHEET-${timesheetId}` }]));
      const result = await refetch();

      if (!leaveProtection.isNavigatingAway.current) {
        const newEntries = result.data?.entries || [];
        const hasAllocations = allocationData.workers.some((w) => w.allocations.length > 0);
        if (timesheet.status === "draft" && hasAllocations) {
          const hasMismatch = newEntries.some((entry) => {
            const worker = allocationData.workers.find((w) => w.workerId === entry.worker.id);
            return worker && worker.allocations.length > 0 && worker.totalAllocated !== entry.work_hours;
          });
          if (hasMismatch) setShowRecalcPrompt(true);
        }
      }
    } catch (err) {
      setPromptState({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.saveErrorText", t),
        variant: "error",
      });
    } finally {
      setIsSaving(false);
    }
  }

  async function handleApproveConfirm() {
    if (!timesheet) return;
    try {
      await approveTimesheet(timesheet.id).unwrap();
      setConfirmApprove(false);
      setPromptState({
        title: t("messages.approveSuccessTitle"),
        text: t("messages.approveSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setConfirmApprove(false);
      const errorData = (err as Record<string, unknown>)?.data as Record<string, unknown> | undefined;
      if (errorData?.details && Array.isArray(errorData.details)) {
        const workerLines = (errorData.details as Array<Record<string, unknown>>)
          .map(
            (d) =>
              `${d.worker_name}: ${d.allocated_hours}/${d.work_hours}${t("allocations.table.hoursShort")} (${t("allocations.errors.missing")}: ${d.difference}${t("allocations.table.hoursShort")})`,
          )
          .join("\n");
        setPromptState({
          title: t("allocations.errors.incompleteAllocationsTitle"),
          text: `${t("allocations.errors.incompleteAllocationsText")}\n\n${workerLines}`,
          variant: "warning",
        });
      } else {
        setPromptState({
          title: t("messages.errorTitle"),
          text: extractErrorMessage(err, "messages.approveErrorText", t),
          variant: "error",
        });
      }
    }
  }

  async function handleDeleteTimesheetConfirm() {
    if (!timesheet) return;
    try {
      await deleteTimesheet(timesheet.id).unwrap();
      setConfirmDeleteTimesheet(false);
      navigate(ACCOUNTING_TIMESHEETS_PAGE_ROUTE);
    } catch (err: unknown) {
      setConfirmDeleteTimesheet(false);
      setPromptState({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.deleteErrorText", t),
        variant: "error",
      });
    }
  }

  function handleApproveClick() {
    setConfirmApprove(true);
  }

  function handleDownloadDocxClick() {
    if (isDownloading || !timesheet) return;
    handleDownloadDocx(timesheet.id, timesheet.year, timesheet.month);
  }

  function handleDownloadPdfClick() {
    if (isDownloading || !timesheet) return;
    handleDownloadPdf(timesheet.id, timesheet.year, timesheet.month);
  }

  function handleCellChange(entryId: number, day: string, code: string) {
    if (!timesheet) return;
    const entry = timesheet.entries?.find((e) => e.id === entryId);
    if (!entry) return;

    const currentData = pendingChanges.get(entryId) || entry.daily_data;
    const newData: DailyData = { ...currentData, [day]: code as DailyData[string] };

    setPendingChanges(new Map(pendingChanges).set(entryId, newData));
  }

  function getCellValue(entryId: number, day: string): string {
    if (!timesheet) return "";
    const entry = timesheet.entries?.find((e) => e.id === entryId);
    if (!entry) return "";
    const pendingData = pendingChanges.get(entryId);
    return pendingData?.[day] ?? entry.daily_data[day] ?? "";
  }

  function getEntryStats(entryId: number) {
    if (!timesheet) {
      return getEntryStatsUtil(undefined);
    }
    const entry = timesheet.entries?.find((e) => e.id === entryId);
    if (!entry) return getEntryStatsUtil(undefined);

    const pendingData = pendingChanges.get(entryId);
    if (!pendingData) return getEntryStatsUtil(entry);

    let workDays = 0;
    let workHours = 0;
    let overtimeHours = 0;
    let annualLeaveDays = 0;
    let medicalLeaveDays = 0;
    let businessTripDays = 0;
    let unpaidLeaveDays = 0;

    const dailyData = pendingData;
    for (const day of Object.keys(dailyData)) {
      const code = dailyData[day];
      if (!code) continue;
      const num = Number(code);
      if (!isNaN(num) && num > 0) {
        workDays++;
        workHours += num;
        if (num > entry.standard_hours_per_day) {
          overtimeHours += num - entry.standard_hours_per_day;
        }
      } else if (code === "О") {
        annualLeaveDays++;
      } else if (code === "Б") {
        medicalLeaveDays++;
      } else if (code === "К") {
        businessTripDays++;
      } else if (code === "А") {
        unpaidLeaveDays++;
      }
    }

    return { work_days: workDays, work_hours: workHours, business_trip_days: businessTripDays, holiday_weekend_hours: 0, overtime_hours: overtimeHours, medical_leave_days: medicalLeaveDays, annual_leave_days: annualLeaveDays, unpaid_leave_days: unpaidLeaveDays, study_leave_days: 0, other_absence_days: 0 };
  }

  function handleTabSwitch(tab: ActiveTab) {
    if (activeTab === "step1" && tab !== "step1" && hasPendingChanges()) {
      setUnsavedTargetTab(tab);
      return;
    }
    if (tab === "step2" && !mismatchDismissedRef.current &&
        allocationData.workers.some((w) => w.allocations.length > 0 && w.totalAllocated !== w.workHours)) {
      setMismatchTargetTab(tab);
      setShowMismatchRecalc(true);
      return;
    }
    setActiveTab(tab);
  }

  async function handleSaveAndSwitch() {
    if (!unsavedTargetTab) return;
    await handleSaveChanges();
    setActiveTab(unsavedTargetTab);
    setUnsavedTargetTab(null);
  }

  async function handleRecalcConfirm() {
    setShowRecalcPrompt(false);
    const entries = timesheet?.entries || [];
    for (const worker of allocationData.workers) {
      const entry = entries.find((e) => e.worker.id === worker.workerId);
      const targetHours = entry?.work_hours ?? worker.workHours;
      if (worker.totalAllocated !== targetHours && worker.allocations.length > 0) {
        const recomputed = recomputeAllocations(
          worker.allocations.map((a) => ({ project_id: a.project_id, hours: a.hours })),
          targetHours,
        );
        if (recomputed.length > 0) await allocationData.handleBulkSave(worker.workerId, recomputed);
      }
    }
  }

  async function handleMismatchRecalcConfirm() {
    setShowMismatchRecalc(false);
    const entries = timesheet?.entries || [];
    for (const worker of allocationData.workers) {
      const entry = entries.find((e) => e.worker.id === worker.workerId);
      const targetHours = entry?.work_hours ?? worker.workHours;
      if (worker.totalAllocated !== targetHours && worker.allocations.length > 0) {
        const recomputed = recomputeAllocations(
          worker.allocations.map((a) => ({ project_id: a.project_id, hours: a.hours })),
          targetHours,
        );
        if (recomputed.length > 0) await allocationData.handleBulkSave(worker.workerId, recomputed);
      }
    }
    if (mismatchTargetTab) setActiveTab(mismatchTargetTab);
    setMismatchTargetTab(null);
  }

  function handleMismatchRecalcCancel() {
    setShowMismatchRecalc(false);
    mismatchDismissedRef.current = true;
    if (mismatchTargetTab) setActiveTab(mismatchTargetTab);
    setMismatchTargetTab(null);
  }

  function handleGoToAllocations() {
    setActiveTab("step2");
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.getElementById("allocations-section")?.scrollIntoView({ behavior: "smooth" });
      });
    });
  }

  function handleGeneratePayroll() {
    if (!timesheet || isGenerating || !timesheet.entries?.length) return;
    setShowGenerateModal(true);
  }

  async function handleGeneratePayrollConfirm(applyMrpDeductions: Record<string, boolean>) {
    if (!timesheet) return;
    try {
      await generatePayroll({
        timesheet_id: timesheet.id,
        apply_mrp_deductions: applyMrpDeductions,
      }).unwrap();
      setShowGenerateModal(false);
      setPromptState({
        title: t("payroll.messages.generateSuccessTitle"),
        text: t("payroll.messages.generateSuccessText"),
        variant: "success",
      });
      setActiveTab("step3");
    } catch (err: unknown) {
      setPromptState({
        title: t("payroll.messages.errorTitle"),
        text: extractErrorMessage(err, "payroll.messages.generateErrorText", t),
        variant: "error",
      });
    }
  }

  async function handleApprovePayrollConfirm() {
    if (!payroll) return;
    try {
      await approvePayroll({ id: payroll.id }).unwrap();
      setConfirmApprovePayroll(false);
      setPromptState({
        title: t("payroll.messages.approveSuccessTitle"),
        text: t("payroll.messages.approveSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setConfirmApprovePayroll(false);
      if (isBudgetLimitErrorV2(err)) {
        if (err.data.failure_type === "invalid_input") {
          setPromptState({
            title: t("payroll.messages.errorTitle"),
            text: tSmeta("errors.invalid_input"),
            variant: "error",
          });
          return;
        }
        if (isOverrideableError(err.data)) {
          payrollOverride.request(err.data, { payrollId: payroll.id });
          return;
        }
        setPromptState({
          title: tSmeta("messages.errorTitle"),
          text: extractSmetaErrorMessage(err.data, tSmeta),
          variant: "error",
        });
        return;
      }
      setPromptState({
        title: t("payroll.messages.errorTitle"),
        text: extractErrorMessage(err, "payroll.messages.approveErrorText", t),
        variant: "error",
      });
    }
  }

  async function handlePayrollOverrideConfirm(overrideFields: { override_reason: string }) {
    if (!payrollOverride.pending) return;
    const { payrollId } = payrollOverride.pending.retryArgs;
    payrollOverride.clear();
    try {
      await approvePayroll({ id: payrollId, body: { override_reason: overrideFields.override_reason } }).unwrap();
      setPromptState({
        title: t("payroll.messages.approveSuccessTitle"),
        text: t("payroll.messages.approveSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      if (isBudgetLimitErrorV2(err)) {
        if (err.data.failure_type === "invalid_input") {
          setPromptState({
            title: t("payroll.messages.errorTitle"),
            text: tSmeta("errors.invalid_input"),
            variant: "error",
          });
          return;
        }
        if (isOverrideableError(err.data)) {
          payrollOverride.request(err.data, { payrollId });
          return;
        }
        setPromptState({
          title: tSmeta("messages.errorTitle"),
          text: extractSmetaErrorMessage(err.data, tSmeta),
          variant: "error",
        });
      } else {
        setPromptState({
          title: t("payroll.messages.errorTitle"),
          text: extractErrorMessage(err, "payroll.messages.approveErrorText", t),
          variant: "error",
        });
      }
    }
  }

  async function handleMarkPaidConfirm() {
    if (!payroll) return;
    try {
      await markPayrollPaid({ id: payroll.id }).unwrap();
      setConfirmMarkPaid(false);
      setPromptState({
        title: t("payroll.messages.markPaidSuccessTitle"),
        text: t("payroll.messages.markPaidSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setConfirmMarkPaid(false);
      setPromptState({
        title: t("payroll.messages.errorTitle"),
        text: extractErrorMessage(err, "payroll.messages.markPaidErrorText", t),
        variant: "error",
      });
    }
  }

  async function handleDeletePayrollConfirm() {
    if (!payroll) return;
    try {
      await deletePayroll(payroll.id).unwrap();
      setConfirmDeletePayroll(false);
      setPromptState({
        title: t("payroll.messages.deleteSuccessTitle"),
        text: t("payroll.messages.deleteSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setConfirmDeletePayroll(false);
      setPromptState({
        title: t("payroll.messages.errorTitle"),
        text: extractErrorMessage(err, "payroll.messages.deleteErrorText", t),
        variant: "error",
      });
    }
  }

  if (isLoading || !timesheet) {
    return (
      <>
        <title>{t("meta.title")}</title>
        <section className="h-full overflow-hidden">
          <div className="h-full component-scroll">
            <TimesheetDetailSkeleton />
          </div>
        </section>
      </>
    );
  }

  const hasEntries = timesheet.entries && timesheet.entries.length > 0;
  const isDraft = timesheet.status === "draft";
  const isStep3Disabled = !payroll;

  const payrollCanApprove = payroll?.status === "calculated" && canApprovePayrollPerm;
  const payrollCanMarkPaid = payroll?.status === "approved" && canPayPayroll;
  const payrollCanDelete = (payroll?.status === "draft" || payroll?.status === "calculated") && canDeletePayrollPerm;

  return (
    <>
      <section className="h-full overflow-hidden">
        <div className="h-full component-scroll">
          <TimesheetDetailHeader
            month={timesheet.month}
            year={timesheet.year}
            locale={locale}
            status={timesheet.status}
            combinedStatus={combinedStatus}
            activeTab={activeTab}
            canEdit={canEdit}
            canApprove={canApprove}
            hasChanges={activeTab === "step1" && hasPendingChanges()}
            isSaving={isSaving}
            isApproving={isApproving}
            isApproveDisabled={!allocationData.allAllocationsMatched}
            isAnyLoading={isAnyLoading}
            onSave={handleSaveChanges}
            onApprove={handleApproveClick}
            onDownloadPdf={handleDownloadPdfClick}
            onDownloadDocx={handleDownloadDocxClick}
            hasPayroll={!!payroll}
            canGeneratePayroll={!!canGeneratePayroll}
            onGeneratePayroll={handleGeneratePayroll}
            isGenerating={isGenerating}
            canApprovePayroll={!!payrollCanApprove}
            onApprovePayroll={() => setConfirmApprovePayroll(true)}
            isApprovingPayroll={isApprovingPayroll}
            canMarkPaid={!!payrollCanMarkPaid}
            onMarkPaid={() => setConfirmMarkPaid(true)}
            isMarkingPaid={isMarkingPaid}
            canDeletePayroll={!!payrollCanDelete}
            onDeletePayroll={() => setConfirmDeletePayroll(true)}
            isDeletingPayroll={isDeletingPayroll}
            onDeleteTimesheet={() => setConfirmDeleteTimesheet(true)}
            isDeletingTimesheet={isDeletingTimesheet}
          />

          {!allocationData.allAllocationsMatched && isDraft && (
            <div className="mb-5">
              <Toast
                color="notice"
                text={t("notices.cannotApprove")}
                additionalText={t("notices.cannotApproveDetail")}
                closable
                autoClose={false}
                isFullWidth
                action={{
                  label: t("notices.goToAllocations"),
                  onClick: handleGoToAllocations,
                }}
              />
            </div>
          )}

          {hasEntries && (
            <div className="flex flex-wrap justify-start items-center gap-2 mb-4 p-6 radius-3xl surface-base-fill">
              {STEP_TABS.map((tab) => {
                const isActive = activeTab === tab.id;
                const isDisabled = tab.id === "step3" && isStep3Disabled;
                const Icon = tab.icon;

                if (isDisabled) {
                  return (
                    <div
                      key={tab.id}
                      className="relative"
                      onMouseEnter={() => setStep3TooltipOpen(true)}
                      onMouseLeave={() => setStep3TooltipOpen(false)}
                    >
                      <Dropdown
                        open={step3TooltipOpen}
                        onClose={() => setStep3TooltipOpen(false)}
                        direction="bottom"
                        align="left"
                        width="w-max"
                        className="elevation-level-2!"
                      >
                        <button
                          type="button"
                          disabled
                          className="px-3 py-2.5 flex justify-start items-center gap-2 radius-sm text-label-md opacity-40 cursor-not-allowed surface-component-fill content-action-neutral"
                        >
                          <Icon size={20} color="currentColor" />
                          {t(tab.labelKey)}
                        </button>
                        <div
                          className="p-2 max-w-content-sm"
                          onMouseEnter={() => setStep3TooltipOpen(true)}
                          onMouseLeave={() => setStep3TooltipOpen(false)}
                        >
                          <p className="text-label-xs content-action-neutral">
                            {t("steps.step3DisabledTooltip")}
                          </p>
                        </div>
                      </Dropdown>
                    </div>
                  );
                }

                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => handleTabSwitch(tab.id)}
                    className={cn(
                      "px-3 py-2.5 flex justify-start items-center gap-2 radius-sm text-label-md cursor-pointer",
                      isActive ? "background-brand-fill text-white" : "surface-component-fill content-action-neutral",
                    )}>
                    <Icon size={20} color="currentColor" />
                    {t(tab.labelKey)}
                  </button>
                );
              })}
            </div>
          )}

          {activeTab !== "step3" && (
            <TimesheetMonthStats
              timesheetId={timesheet.id}
              calendarDays={timesheet.total_calendar_days}
              holidayDays={timesheet.month_holiday_days}
              workDays={timesheet.month_work_days}
              workHours={Number(timesheet.month_work_hours_8h)}
              canEdit={canEdit}
              onSaveSuccess={(title, text) => setPromptState({ title, text, variant: "success" })}
              onSaveError={(title, text) => setPromptState({ title, text, variant: "error" })}
            />
          )}

          {activeTab === "step1" && (
            <>
              {!helpDismissed && (
                <div className="mb-4">
                  <Toast
                    color="grey"
                    text={t("detail.helpTitle")}
                    additionalText={t("detail.helpText")}
                    closable
                    autoClose={false}
                    isFullWidth
                    onClose={() => {
                      localStorage.setItem("timesheet-help-dismissed", "true");
                      setHelpDismissed(true);
                    }}
                  />
                </div>
              )}
              <div className="p-7 surface-base-fill radius-3xl">
                <StatusCodeLegend />
                <HolidaysLegend month={timesheet.month} />
                <Suspense fallback={<TimesheetTableSkeleton />}>
                  <TimesheetEditableTable
                    entries={timesheet.entries || []}
                    daysInMonth={daysInMonth}
                    year={timesheet.year}
                    month={timesheet.month}
                    canEdit={canEdit}
                    getCellValue={getCellValue}
                    onCellChange={handleCellChange}
                    getEntryStats={getEntryStats}
                  />
                </Suspense>
              </div>
            </>
          )}

          {activeTab === "step2" && hasEntries && (
            <div id="allocations-section">
              <TimesheetAllocationsSection {...allocationData} />
            </div>
          )}

          {activeTab === "step3" && payroll && (
            <div className="flex flex-col gap-3">
              <Suspense fallback={<TimesheetDetailSkeleton />}>
                <PayrollStatCards
                  gross={payroll.total_gross_salary}
                  employeeDeductions={payroll.total_employee_deductions}
                  employerContributions={payroll.total_employer_contributions}
                  net={payroll.total_net_salary}
                  employerCost={payroll.total_employer_cost}
                  monthWorkDays={payroll.timesheet?.month_work_days || timesheet.month_work_days}
                />
              </Suspense>

              <Suspense fallback={<TimesheetDetailSkeleton />}>
                <AccrualSection
                  entries={payroll.entries}
                  onViewEntryDetails={(entry) => setEntryDetailModal(entry)}
                  locale={locale}
                  onGoToStep1={() => setActiveTab("step1")}
                />
              </Suspense>

              <div className="grid grid-cols-[1fr_1fr] gap-3 items-stretch">
                <Suspense fallback={<TimesheetDetailSkeleton />}>
                  <AccountingSection
                    opv={payroll.total_opv}
                    vosms={payroll.total_vosms}
                    ipn={payroll.total_ipn}
                    opvr={payroll.total_opvr}
                    oppv={payroll.total_oppv}
                    so={payroll.total_so}
                    oosms={payroll.total_oosms}
                    sn={payroll.total_sn}
                    totalEmployeeDeductions={payroll.total_employee_deductions}
                    totalEmployerContributions={payroll.total_employer_contributions}
                    constants_used={payroll.constants_used}
                  />
                </Suspense>

                <Suspense fallback={<TimesheetDetailSkeleton />}>
                  <ReportingSection
                    entries={payroll.entries}
                    locale={locale}
                  />
                </Suspense>
              </div>
            </div>
          )}
        </div>
      </section>

      {isDownloading && (
        <Prompt
          loaderMode={true}
          onClose={() => {}}
          title=""
          text=""
          loaderText={t("messages.loader.waitingForDownload.text")}
          additionalText={t("messages.loader.waitingForDownload.additionalText")}
          namespace="TimesheetsPage"
        />
      )}

      {promptState && (
        <Prompt
          title={promptState.title}
          text={promptState.text}
          variant={promptState.variant}
          onClose={() => setPromptState(null)}
          namespace="TimesheetsPage"
        />
      )}

      {confirmApprove && (
        <PromptForm
          title={t("confirm.approveTitle")}
          text={t("confirm.approveText")}
          variant="warning"
          onClose={() => setConfirmApprove(false)}
          onConfirm={handleApproveConfirm}
          isLoading={isApproving}
          confirmText={t("confirm.approveConfirm")}
          cancelText={t("confirm.cancel")}
          namespace="TimesheetsPage"
        />
      )}

      {confirmDeleteTimesheet && (
        <PromptForm
          title={t("confirm.deleteTitle")}
          text={t("confirm.deleteText")}
          variant="error"
          onClose={() => setConfirmDeleteTimesheet(false)}
          onConfirm={handleDeleteTimesheetConfirm}
          isLoading={isDeletingTimesheet}
          confirmText={t("confirm.deleteConfirm")}
          cancelText={t("confirm.cancel")}
          namespace="TimesheetsPage"
        />
      )}

      {confirmApprovePayroll && (
        <PromptForm
          title={t("payroll.confirm.approveTitle")}
          text={t("payroll.confirm.approveText")}
          variant="warning"
          onClose={() => setConfirmApprovePayroll(false)}
          onConfirm={handleApprovePayrollConfirm}
          isLoading={isApprovingPayroll}
          confirmText={t("payroll.confirm.approveConfirm")}
          cancelText={t("payroll.confirm.cancel")}
          namespace="TimesheetsPage"
        />
      )}

      {payrollOverride.pending && (
        <Suspense fallback={null}>
          <OverrideReasonModal
            errorData={payrollOverride.pending.errorData}
            onClose={payrollOverride.clear}
            onConfirm={handlePayrollOverrideConfirm}
            isLoading={isApprovingPayroll}
          />
        </Suspense>
      )}

      {confirmMarkPaid && (
        <PromptForm
          title={t("payroll.confirm.markPaidTitle")}
          text={t("payroll.confirm.markPaidText")}
          variant="warning"
          onClose={() => setConfirmMarkPaid(false)}
          onConfirm={handleMarkPaidConfirm}
          isLoading={isMarkingPaid}
          confirmText={t("payroll.confirm.markPaidConfirm")}
          cancelText={t("payroll.confirm.cancel")}
          namespace="TimesheetsPage"
        />
      )}

      {confirmDeletePayroll && (
        <PromptForm
          title={t("payroll.confirm.deleteTitle")}
          text={t("payroll.confirm.deleteText")}
          variant="error"
          onClose={() => setConfirmDeletePayroll(false)}
          onConfirm={handleDeletePayrollConfirm}
          isLoading={isDeletingPayroll}
          confirmText={t("payroll.confirm.deleteConfirm")}
          cancelText={t("payroll.confirm.cancel")}
          namespace="TimesheetsPage"
        />
      )}

      {unsavedTargetTab !== null && (
        <PromptForm
          title={t("confirm.unsavedTitle")}
          text={t("confirm.unsavedText")}
          variant="warning"
          onClose={() => setUnsavedTargetTab(null)}
          onConfirm={handleSaveAndSwitch}
          isLoading={isSaving}
          confirmText={t("confirm.unsavedConfirm")}
          cancelText={t("confirm.cancel")}
          namespace="TimesheetsPage"
        />
      )}

      {showGenerateModal && timesheet?.entries && (
        <Suspense fallback={null}>
          <GeneratePayrollModal
            entries={timesheet.entries}
            previousPayrollEntries={payroll?.entries}
            onClose={() => setShowGenerateModal(false)}
            onConfirm={handleGeneratePayrollConfirm}
            isLoading={isGenerating}
          />
        </Suspense>
      )}

      {entryDetailModal && (
        <Suspense fallback={null}>
          <PayrollEntryDetailModal
            entry={entryDetailModal}
            constants_used={payroll?.constants_used}
            onClose={() => setEntryDetailModal(null)}
            locale={locale}
            month={timesheet.month}
            year={timesheet.year}
            payrollStatus={payroll?.status || "draft"}
            currentCashBalance={payroll?.current_cash_balance}
          />
        </Suspense>
      )}

      {leaveProtection.modalOpen && (
        <UnsavedChangesModal
          onSaveAndLeave={leaveProtection.handleSaveAndLeave}
          onLeaveWithout={leaveProtection.handleLeaveWithout}
          onStay={leaveProtection.handleStay}
          isSaving={leaveProtection.isSaving}
        />
      )}

      {showRecalcPrompt && unsavedTargetTab === null && (
        <PromptForm
          title={t("allocations.recalc.title")}
          text={t("allocations.recalc.text")}
          variant="warning"
          onClose={() => setShowRecalcPrompt(false)}
          onConfirm={handleRecalcConfirm}
          confirmText={t("allocations.recalc.confirm")}
          cancelText={t("allocations.recalc.cancel")}
          namespace="TimesheetsPage"
        />
      )}

      {showMismatchRecalc && (
        <PromptForm
          title={t("allocations.recalc.mismatchTitle")}
          text={t("allocations.recalc.mismatchText")}
          variant="warning"
          onClose={handleMismatchRecalcCancel}
          onConfirm={handleMismatchRecalcConfirm}
          confirmText={t("allocations.recalc.confirm")}
          cancelText={t("allocations.recalc.skip")}
          namespace="TimesheetsPage"
        />
      )}

    </>
  );
}
